import type { ColumnDef } from '@tanstack/react-table'
import type { TFunction } from 'i18next'

import { TimeAgo, type DataTableColumnMeta } from '@/shared/ui'
import { StatusBadge } from '@/shared/ui'

import { leadTypeTone } from '../model/lead-badge'
import type { LeadListItem } from '../model/types'

export function leadColumns(t: TFunction<'leads'>): ColumnDef<LeadListItem, unknown>[] {
  return [
    {
      accessorKey: 'createdAt',
      header: t('columns.created'),
      size: 170,
      enableSorting: false,
      meta: { isMono: true } satisfies DataTableColumnMeta,
      cell: ({ row }) => <TimeAgo value={row.original.createdAt} />,
    },
    {
      id: 'type',
      header: t('columns.type'),
      size: 120,
      enableSorting: false,
      cell: ({ row }) => (
        <StatusBadge tone={leadTypeTone(row.original.type)} size="sm">
          {t(`type.${row.original.type}`, { defaultValue: row.original.type })}
        </StatusBadge>
      ),
    },
    {
      accessorKey: 'ip',
      header: t('columns.ip'),
      size: 130,
      enableSorting: false,
      meta: { isMono: true } satisfies DataTableColumnMeta,
      cell: ({ row }) => (row.original.ip === '' ? '—' : row.original.ip),
    },
    {
      id: 'served',
      header: t('filters.served'),
      size: 90,
      enableSorting: false,
      cell: ({ row }) => t(`page.${row.original.page}`, { defaultValue: row.original.page }),
    },
    {
      id: 'country',
      header: t('columns.country'),
      size: 140,
      enableSorting: false,
      cell: ({ row }) =>
        [row.original.country, row.original.city].filter((part) => part !== '').join(' · '),
    },
    {
      accessorKey: 'source',
      header: t('columns.source'),
      size: 140,
      enableSorting: false,
      cell: ({ row }) => t(`common:source.${row.original.source}`, { defaultValue: row.original.source }),
    },
    {
      accessorKey: 'blockReason',
      header: t('columns.reason'),
      enableSorting: false,
      // `none` is «nothing filtered this», which is the absence of a reason
      // rather than one of them. The empty string is what the column held
      // before `block_reason` became an enum; rows predating that still exist.
      cell: ({ row }) =>
        row.original.blockReason === '' || row.original.blockReason === 'none'
          ? '—'
          : t(`blockReason.${row.original.blockReason}`, {
              defaultValue: row.original.blockReason,
            }),
    },
  ]
}
